import { useCallback, useId, useRef } from 'react'

import type { Uploader } from '@buffer-mono/uploader'

import { makeCanvaDesignOpenCallbackUrl } from '~publish/services/callbacks'

import { useCanvaDesignConfig } from './util/config'
import type { ServiceForCanva } from './util/interface'
import { useTrackingMetadata, useTracking } from './util/tracking'
import { uploadCanvaDesign } from './util/uploadCanvaDesign'
import { useBroadcastChannel } from './util/useBroadcastChannel'

type UseOpenCanvaOptions = {
  // For backwards compatability - older canva assets will not be
  // visible to the user, so we need to fetch the media from the
  // original url.
  mediaFetchUrl?: string
  uploader: Pick<Uploader, 'addFilesFromUrls'>
  isStoryPost?: boolean
  service: ServiceForCanva
  designId?: string
  onClose?: () => void
  onRemoveImage?: () => void
}

export type CanvaOpenOptions = {
  /** Overrides the channel default design dimensions */
  dimensions?: {
    width: string
    height: string
  }
  /** Human-readable ratio of the chosen dimensions, used for tracking (ex. 4:5) */
  ratio?: string
}

export function useOpenCanva(
  options: UseOpenCanvaOptions,
): (openOptions?: CanvaOpenOptions) => void {
  const {
    uploader,
    service,
    isStoryPost,
    designId,
    onClose,
    mediaFetchUrl,
    onRemoveImage,
  } = options
  const designConfig = useCanvaDesignConfig(service, isStoryPost)
  const trackingMetadata = useTrackingMetadata(service, designId)
  const tracking = useTracking(designId ? 'edit' : 'create')

  // unique id for the editing session
  // used to correlate return state from the new tab
  const sessionId = useId()
  const childWindowRef = useRef<Window | null>(null)

  // subscribe to the broadcast channel to receive messages from the new tab on completion
  useBroadcastChannel(
    sessionId,
    async (message) => {
      const newDesignId = message.designId

      if (newDesignId !== designId) {
        // canva design has been replaced! Delete the old design
        onRemoveImage?.()
      }

      childWindowRef.current?.close()
      childWindowRef.current = null
      const newMetadata = { ...trackingMetadata, designId: newDesignId }
      tracking.designClose(newMetadata)
      await uploadCanvaDesign(newDesignId, message.exportUrl, uploader)
      tracking.attached(newMetadata)
    },
    [tracking, trackingMetadata, uploader],
  )

  // provide the callback to trigger the design edit/open flow
  return useCallback(
    (openOptions?: CanvaOpenOptions) => {
      const dimensions = openOptions?.dimensions ?? designConfig.dimensions
      const openUrl = makeCanvaDesignOpenCallbackUrl({
        designId: designId ?? 'new',
        width: dimensions.width,
        height: dimensions.height,
        title: designConfig.title,
        sessionId,
        mediaFetchUrl,
      })
      tracking.clicked(
        openOptions?.ratio
          ? { ...trackingMetadata, ratio: openOptions.ratio }
          : trackingMetadata,
      )
      // Open as a popup sized to 80% of the current browser window (scaling both
      // dimensions equally preserves the window's aspect ratio) and centered over
      // it. Using window.screenX/Y + outerWidth/Height (rather than
      // screen.width/height) keeps it centered on the active window, including
      // multi-monitor setups.
      const popupScale = 0.8
      const width = Math.round(window.outerWidth * popupScale)
      const height = Math.round(window.outerHeight * popupScale)
      const left = Math.round(window.screenX + (window.outerWidth - width) / 2)
      const top = Math.round(window.screenY + (window.outerHeight - height) / 2)
      const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      childWindowRef.current = window.open(openUrl, '_blank', features)
      onClose?.()
    },
    [
      tracking,
      trackingMetadata,
      designConfig,
      designId,
      sessionId,
      mediaFetchUrl,
      onClose,
    ],
  )
}
